import { useEffect, useState, useCallback } from "react";
import PageShell from "@/components/PageShell";
import api, { apiError } from "@/lib/api";
import { formatRupiah, formatDate, formatDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import { Check, X, ClockAlert } from "lucide-react";
import { UserTag } from "@/components/UserTag";

export default function Approvals() {
  const [reschedules, setReschedules] = useState([]);
  const [changes, setChanges] = useState([]);

  const load = useCallback(() => {
    api.get("/reschedules?status=pending").then((r) => setReschedules(r.data));
    api.get("/change-requests?status=pending").then((r) => setChanges(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (url, okMsg) => {
    try { await api.post(url); toast.success(okMsg); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const TYPE_LABEL = { schedule_date: "Ubah Tanggal", schedule_warkat: "Ubah Data Warkat", schedule_amount: "Revisi Nominal / Kredit", invoice_edit: "Koreksi Nota", invoice_cancel: "Pembatalan Nota", tranche_reschedule: "Reschedule Termin BG", schedule_restructure: "Ubah / Split Pembayaran" };
  const fmtTr = (t) => `${formatRupiah(t.amount)} · ${t.method === "giro" ? `Giro ${t.warkat_number || ""} ${t.warkat_bank || ""}` : "Transfer"} · ${formatDate(t.cair_date)}`;

  return (
    <PageShell title="Persetujuan Owner">
      <Card className="shadow-sm">
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><ClockAlert className="h-4 w-4" />Pengajuan Reschedule ({reschedules.length})</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {reschedules.map((r) => (
            <div key={r.id} className="border rounded-lg p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`approve-resched-${r.code}`}>
              <div className="text-sm space-y-0.5">
                <div className="font-semibold">{r.code} · dari {r.from_schedule_code}</div>
                <div className="text-slate-600">Metode: {r.old_method} → <b>{r.new_method}</b> · Tanggal: {formatDate(r.old_payment_date)} → <b>{formatDate(r.new_payment_date)}</b></div>
                {r.new_method === "giro" && <div className="text-slate-500 text-xs">Warkat baru: {r.new_warkat_bank} · {r.new_warkat_number}</div>}
                <div className="text-slate-500 text-xs flex flex-wrap items-center gap-1">Alasan: {r.reason} · Pemohon: <UserTag id={r.requester_id} name={r.requester} testid={`approve-resched-requester-${r.code}`} /> · {formatDateTime(r.requested_at)}</div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => act(`/reschedules/${r.id}/approve`, "Reschedule disetujui")} data-testid={`reschedule-approve-button-${r.code}`}><Check className="h-4 w-4 mr-1" />Setujui</Button>
                <Button size="sm" variant="destructive" onClick={() => act(`/reschedules/${r.id}/reject`, "Reschedule ditolak")} data-testid={`reschedule-reject-button-${r.code}`}><X className="h-4 w-4 mr-1" />Tolak</Button>
              </div>
            </div>
          ))}
          {reschedules.length === 0 && <div className="text-sm text-slate-400 py-4 text-center">Tidak ada pengajuan reschedule.</div>}
        </CardContent>
      </Card>

      <Card className="shadow-sm">
        <CardHeader><CardTitle className="text-base">Pengajuan Perubahan Data Terkunci ({changes.length})</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {changes.map((c) => (
            <div key={c.id} className="border rounded-lg p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`approve-change-${c.code}`}>
              <div className="text-sm space-y-0.5">
                <div className="font-semibold">{c.code} · {TYPE_LABEL[c.type] || c.type} · {c.schedule_code ? `Jadwal ${c.schedule_code}` : `Nota ${c.invoice_code}`}</div>
                {c.type === "schedule_restructure" ? (
                  <div className="text-xs space-y-0.5" data-testid={`approve-restructure-detail-${c.code}`}>
                    <div className="text-slate-500">Lama: {c.old_data.tranches?.length ? `Split ${c.old_data.tranches.length} termin` : `${c.old_data.method} · ${formatDate(c.old_data.payment_date)}`} · {formatRupiah(c.old_data.net_amount)}</div>
                    {c.new_data.tranches.map((t, i) => <div key={i} className="text-slate-700">Termin baru {(c.old_data.tranches || []).filter((x) => x.cleared).length + i + 1}: <b>{fmtTr(t)}</b></div>)}
                  </div>
                ) : (<>
                <div className="text-slate-500 text-xs">Lama: {JSON.stringify(c.old_data)}</div>
                <div className="text-slate-600 text-xs">Baru: {JSON.stringify(c.new_data)}</div>
                </>)}
                <div className="text-slate-500 text-xs flex flex-wrap items-center gap-1">Alasan: {c.reason} · Pemohon: <UserTag id={c.requester_id} name={c.requester} testid={`approve-change-requester-${c.code}`} /></div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => act(`/change-requests/${c.id}/approve`, "Perubahan disetujui")} data-testid={`change-approve-button-${c.code}`}><Check className="h-4 w-4 mr-1" />Setujui</Button>
                <Button size="sm" variant="destructive" onClick={() => act(`/change-requests/${c.id}/reject`, "Perubahan ditolak")} data-testid={`change-reject-button-${c.code}`}><X className="h-4 w-4 mr-1" />Tolak</Button>
              </div>
            </div>
          ))}
          {changes.length === 0 && <div className="text-sm text-slate-400 py-4 text-center">Tidak ada pengajuan perubahan.</div>}
        </CardContent>
      </Card>
    </PageShell>
  );
}
